import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Icon from '../forms/Icon';
import { addSpacesToPascalCase, generateId, translate } from '../utility/functions';
import Select from '../forms/Select';
import Input from '../forms/Input';
import { TableContext } from './Table';

const ColSpace = styled.div`
    position: absolute;
    height: 100%;
    top: 0;
    cursor: col-resize;
    width: 5px;
    display: flex;
    justify-content: center;
    z-index: 1;

    &::before {
        content: '';
        position: absolute; 
        top: 0;
        width: 1px;
        height: 1000%;
        background-color: ${({ theme, dragging }) => theme.gray + (dragging ? '' : '40')};
        opacity: 0;
        transition: opacity 0.2s;
    }
`

const TableHead = styled.th`
    position: relative;

    ${({ dragging }) => dragging ? 'overflow: unset !important; user-select: none;' : ''};

    &:hover {
        & > div::before {
            opacity: 1;
        }
    }
`
const Wrapper = styled.div`
    display: flex;
    cursor: ${({ pointer }) => pointer ? 'pointer' : 'default'};
    margin-bottom: 0.2rem;
    transition: all 0.3s;
    border-radius: 0.2em;
    overflow: hidden;

    &:hover {
        background: ${({ theme }) => theme.highlight}20;
        padding: 0 0.2em;
    }
`
const Chevron = styled(Icon)`
    transition: transform .3s;
    transform: rotate(${({ state }) => state ? '-180deg' : '0deg'});
`

const TableHeader = ({ index, ...props }) => {
    const { master, filter, updateFilter, sort, setSort, disableHeaderSearch, updateSpacing } = useContext(TableContext);
    const { id, propKey, options, type, label, sortBy, spacing } = props;

    const headRef = useRef()
    const [searchValue, setSearchValue] = useState('');
    const [ascending, setAscending] = useState(props.ascending ?? true);

    const [dragging, setDragging] = useState(false);
    const [pos, setPos] = useState(0);

    useLayoutEffect(() => {
        if (!sortBy) return;
        setSort({ propKey, ascending })
    }, [])

    useEffect(() => {
        updateFilter({
            id,
            query: searchValue,
            propKey,
            type: options ? 'exactMatch' : type,
            label
        });
    }, [searchValue])

    useEffect(() => {
        if (sort.propKey !== propKey) {
            setAscending(ascending)
        }
    }, [sort])

    useEffect(() => {
        if (!filter.some(e => e.id === id)) {
            setSearchValue('')
        }
    }, [filter])

    const handleClicked = () => {
        setSort({ propKey, ascending: !ascending })
        setAscending(!ascending)
    }

    const buildOptions = () => {
        let resultOptions;

        if (options instanceof Array) {
            resultOptions = options;
        } else if (typeof options === 'function') {
            resultOptions = options(master);
        } else if (typeof options === 'boolean') {
            resultOptions = [...new Set(master.map(e => e[propKey]))];
        } else {
            resultOptions = options.filter(e => e);
        }

        const isAValue = e => e !== null && e !== undefined && e !== '';

        return resultOptions.filter(isAValue).map(e => ({
            label: translate(e),
            value: e,
        }));
    }

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragging) return;
            const rect = headRef.current.getBoundingClientRect();
            setPos(((e.clientX + 5) - rect.right) * -1);
        };
        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, [dragging])

    const calculateDifference = () => {
        const headWidth = headRef.current.offsetWidth;
        const adjustedPos = Math.abs(pos) > 5 ? pos : 0;
        const widthDifference = headWidth + adjustedPos * -1;
        const newSpacing = Math.max((widthDifference / headWidth) * spacing, 1);
        updateSpacing(id, newSpacing)
        setPos(0);
    }

    return (
        <TableHead
            ref={headRef}
            dragging={dragging}
        >
            <Wrapper
                onClick={handleClicked}
                pointer={propKey && !disableHeaderSearch}
            >
                <div>
                    {label} {propKey ? sort.propKey === propKey && <Chevron state={ascending} icon='chevronDown' /> : null}
                </div>
            </Wrapper>
            {(propKey && !disableHeaderSearch) ?
                options ? (
                    <Select
                        standalone
                        value={searchValue}
                        onInput={(e) => setSearchValue(e.target.value)}
                        options={buildOptions}
                        nullable
                        aria-label='Filter by type'
                    />
                ) : (
                    <Input
                        standalone
                        type='search'
                        placeholder='Search'
                        value={searchValue}
                        onInput={(e) => setSearchValue(e.target.value)}
                        autoCapitalize='none'
                    />
                ) : null}
            <ColSpace
                style={{ right: `${pos}px` }}
                dragging={dragging}
                onMouseDown={() => setDragging(true)}
                onMouseUp={() => {
                    setDragging(false);
                    calculateDifference();
                }}
            />
        </TableHead>
    );
}



export default TableHeader;